import { useQuery } from '@tanstack/react-query';
import { AnalyticsProfileService } from '../../core/services';
import {
  Filters,
  GetAvgTicketPriceResponse,
  GetAvgTimePerJobCardResponse,
  GetBayInformationResponse,
  GetCompletedWorkOrdersResponse,
  GetConsumptionMonthlyTrendResponse,
  GetCustomerBreakdownResponse,
  GetCustomerRatingResponse,
  GetCustomerRetentionResponse,
  GetCustomerVisitPerformanceResponse,
  GetFleetAvailabilityResponse,
  GetHealthKPIsResponse,
  GetJobCardOpenVsClosedResponse,
  GetMonthlyPurchaseOrdersResponse,
  GetNetSalesStationResponse,
  GetNetSalesTechnicianResponse,
  GetOperationalKPIsResponse,
  GetPaymentsOverviewResponse,
  GetRevenueCostProfitResponse,
  GetServiceByVehicleTypeResponse,
  GetServiceOnTimeResponse,
  GetStationExpenseResponse,
  GetStationExpenseTypeResponse,
  GetTargetVsSalesResponse,
  GetTechnicianProductivityResponse,
  GetTopConsumedPartsResponse,
  GetTopSalesResponse,
  GetTopServicesPerformedResponse,
  GetVehicleTopServicesResponse,
  GetVehicleTurnaroundTimeResponse,
  GetWorkInProgressResponse,
} from './interface';

export const useGetNetSalesService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetNetSalesStationResponse, unknown>({
    queryKey: ['analytics-net-sales'],
    queryFn: async () => {
      return AnalyticsProfileService.getNetSales({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetTargetVsSalesService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetTargetVsSalesResponse, unknown>({
    queryKey: ['analytics-target-sales'],
    queryFn: async () => {
      return AnalyticsProfileService.getTargetVsSales({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetWorkInProgressService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetWorkInProgressResponse, unknown>({
    queryKey: ['analytics-work-in-progress'],
    queryFn: async () => {
      return AnalyticsProfileService.getWorkInProgress({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetCompletedWorkOrdersService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetCompletedWorkOrdersResponse, unknown>({
    queryKey: ['analytics-completed-workorders'],
    queryFn: async () => {
      return AnalyticsProfileService.getCompletedWorkOrders({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetRevenueCostProfitService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetRevenueCostProfitResponse, unknown>({
    queryKey: ['analytics-revenue-cost-profit'],
    queryFn: async () => {
      return AnalyticsProfileService.getRevenueCostProfit({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetTopSalesService = (initialParams: Filters, typeName: string) => {
  const { data, isLoading, error, refetch } = useQuery<GetTopSalesResponse, unknown>({
    queryKey: ['analytics-top-sales'],
    queryFn: async () => {
      return AnalyticsProfileService.getTopSales(
        {
          range: initialParams.range,
          stationId: initialParams.stationId,
          allStation: initialParams.allStation,
        },
        typeName
      );
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetNetSalesTechniciansService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetNetSalesTechnicianResponse, unknown>({
    queryKey: ['analytics-net-sales-technicians'],
    queryFn: async () => {
      return AnalyticsProfileService.getNetSalesTechnicians({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetAvgTicketPriceService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetAvgTicketPriceResponse, unknown>({
    queryKey: ['analytics-average-ticket-price'],
    queryFn: async () => {
      return AnalyticsProfileService.getAvgTicketPrice({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetPaymentsOverviewService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetPaymentsOverviewResponse, unknown>({
    queryKey: ['analytics-payments-overview'],
    queryFn: async () => {
      return AnalyticsProfileService.getPaymentsOverview({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetStationExpenseWeeklyService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetStationExpenseResponse, unknown>({
    queryKey: ['analytics-station-expense-weekly'],
    queryFn: async () => {
      return AnalyticsProfileService.getStationExpenseWeekly({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetStationExpenseTypeService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetStationExpenseTypeResponse, unknown>({
    queryKey: ['analytics-station-expense-type'],
    queryFn: async () => {
      return AnalyticsProfileService.getStationExpenseType({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetServiceCancellationReasonsService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetStationExpenseTypeResponse, unknown>({
    queryKey: ['analytics-service-cancellation-reasons'],
    queryFn: async () => {
      return AnalyticsProfileService.getServiceCancellationReasons({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetServiceOnTimeService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetServiceOnTimeResponse, unknown>({
    queryKey: ['analytics-service-on-time'],
    queryFn: async () => {
      return AnalyticsProfileService.getServiceOnTime({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetSOSDataService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetServiceOnTimeResponse, unknown>({
    queryKey: ['analytics-sos-data'],
    queryFn: async () => {
      return AnalyticsProfileService.getSosData({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetTechnicianProductivityService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetTechnicianProductivityResponse, unknown>({
    queryKey: ['analytics-technician-productivity'],
    queryFn: async () => {
      return AnalyticsProfileService.getTechnicianProductivity({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetFleetAvailabilityService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetFleetAvailabilityResponse, unknown>({
    queryKey: ['analytics-fleet-availability-service'],
    queryFn: async () => {
      return AnalyticsProfileService.getFleetAvailability({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
        customerId: initialParams.customerId,
        accountId: initialParams.accountId,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetVehicleTurnaroundService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetVehicleTurnaroundTimeResponse, unknown>({
    queryKey: ['analytics-vehicle-turnaround-service'],
    queryFn: async () => {
      return AnalyticsProfileService.getVehicleTurnaround({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
        customerId: initialParams.customerId,
        accountId: initialParams.accountId,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetBayInformationService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetBayInformationResponse, unknown>({
    queryKey: ['analytics-bay-information'],
    queryFn: async () => {
      return AnalyticsProfileService.getBayInformation({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useAvgTimePerJobCardService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetAvgTimePerJobCardResponse, unknown>({
    queryKey: ['analytics-avg-time-per-jobcard'],
    queryFn: async () => {
      return AnalyticsProfileService.getAvgTimePerJobCard({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetJobCardOpenVsClosedService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetJobCardOpenVsClosedResponse, unknown>({
    queryKey: ['analytics-jobcard-open-vs-closed'],
    queryFn: async () => {
      return AnalyticsProfileService.getJobCardOpenVsClosed({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetCustomerBreakdownService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetCustomerBreakdownResponse, unknown>({
    queryKey: ['analytics-customer-breakdown'],
    queryFn: async () => {
      return AnalyticsProfileService.getCustomerBreakdown({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
        businessId: initialParams.businessId,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetCustomerSummaryService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetCustomerBreakdownResponse, unknown>({
    queryKey: ['analytics-customer-summary'],
    queryFn: async () => {
      return AnalyticsProfileService.getCustomerSummary({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
        businessId: initialParams.businessId,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetTopServicesPerformedService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetTopServicesPerformedResponse, unknown>({
    queryKey: ['analytics-top-services-performed'],
    queryFn: async () => {
      return AnalyticsProfileService.getTopServicesPerformed({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetVehicleTopService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetVehicleTopServicesResponse, unknown>({
    queryKey: ['analytics-vehicle-top-services'],
    queryFn: async () => {
      return AnalyticsProfileService.getVehicleTopServices({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetServiceByVehicleTypeService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetServiceByVehicleTypeResponse, unknown>({
    queryKey: ['analytics-service-by-vehicle-type'],
    queryFn: async () => {
      return AnalyticsProfileService.getServiceByVehicleType({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetCustomerRetentionService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetCustomerRetentionResponse, unknown>({
    queryKey: ['analytics-customer-retention'],
    queryFn: async () => {
      return AnalyticsProfileService.getCustomerRetention({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
        businessId: initialParams.businessId,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetCustomerRatingService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetCustomerRatingResponse, unknown>({
    queryKey: ['analytics-customer-rating'],
    queryFn: async () => {
      return AnalyticsProfileService.getCustomerRating({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
        businessId: initialParams.businessId,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetCustomerVisitPerformanceService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetCustomerVisitPerformanceResponse, unknown>({
    queryKey: ['analytics-customer-visit-performance'],
    queryFn: async () => {
      return AnalyticsProfileService.getCustomerVisitPerformance({
        range: initialParams.range,
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
        businessId: initialParams.businessId,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetTopConsumedPartsService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetTopConsumedPartsResponse, unknown>({
    queryKey: ['analytics-inventory-top-consumed-parts'],
    queryFn: async () => {
      return AnalyticsProfileService.getTopConsumedParts({
        storeId: initialParams.storeId,
        allStore: initialParams.allStore,
        range: initialParams.range,
        limit: initialParams.limit,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetMonthlyPurchaseOrdersService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetMonthlyPurchaseOrdersResponse, unknown>({
    queryKey: ['analytics-inventory-monthly-purchase-orders'],
    queryFn: async () => {
      return AnalyticsProfileService.getMonthlyPurchaseOrders({
        storeId: initialParams.storeId,
        allStore: initialParams.allStore,
        range: initialParams.range,
        limit: initialParams.limit,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetOperationalKPIsService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetOperationalKPIsResponse, unknown>({
    queryKey: ['analytics-inventory-operational-kpis'],
    queryFn: async () => {
      return AnalyticsProfileService.getOperationalKPIs({
        stationId: initialParams.stationId,
        allStation: initialParams.allStation,
        range: initialParams.range,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetHealthKPIsService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetHealthKPIsResponse, unknown>({
    queryKey: ['analytics-inventory-health-kpis'],
    queryFn: async () => {
      return AnalyticsProfileService.getHealthKPIs({
        storeId: initialParams.storeId,
        allStore: initialParams.allStore,
        range: initialParams.range,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetConsumptionMonthlyTrendService = (initialParams: Filters) => {
  const { data, isLoading, error, refetch } = useQuery<GetConsumptionMonthlyTrendResponse, unknown>({
    queryKey: ['analytics-inventory-consumption-monthly-trend'],
    queryFn: async () => {
      return AnalyticsProfileService.getConsumptionMonthlyTrend({
        storeId: initialParams.storeId,
        allStore: initialParams.allStore,
        range: initialParams.range,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
